// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

import { useEffect, useState } from "react";
import type {
  CockpitApi,
  ProjectPolicy,
  ReviewerProfileReference,
} from "./api.js";

export function ReviewerProfileControls({
  api,
  busy,
  canStart,
  hasRun,
  onStart,
}: {
  readonly api: CockpitApi;
  readonly busy: boolean;
  readonly canStart: boolean;
  readonly hasRun: boolean;
  readonly onStart: (profile?: ReviewerProfileReference) => Promise<void>;
}) {
  const [policy, setPolicy] = useState<ProjectPolicy | null>(null);
  const [mode, setMode] = useState("project-default");
  const [protocolId, setProtocolId] = useState("");
  const [contentDigest, setContentDigest] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    void api
      .projectPolicy()
      .then(({ policy: next }) => {
        if (active) setPolicy(next);
      })
      .catch((reason: unknown) => {
        if (active)
          setError(reason instanceof Error ? reason.message : String(reason));
      });
    return () => {
      active = false;
    };
  }, [api]);

  const reference = {
    protocolId: protocolId.trim(),
    contentDigest: contentDigest.trim(),
  };
  const valid =
    /^[a-z][a-z0-9-]{2,63}$/u.test(reference.protocolId) &&
    /^[a-f0-9]{64}$/u.test(reference.contentDigest);
  const disabled = busy || saving;

  async function saveDefault(next: ReviewerProfileReference | null) {
    if (policy === null) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const saved = await api.setDefaultReviewerProfile(policy, next);
      setPolicy(saved.policy);
      setMessage(
        "Project default saved for future reviews. Existing runs retain their selected profile.",
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setSaving(false);
    }
  }

  async function refreshPolicy() {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      setPolicy((await api.projectPolicy()).policy);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      className="panel"
      aria-labelledby="reviewer-profile-controls-title"
    >
      <div className="panel-pad stack">
        <h3 id="reviewer-profile-controls-title">Reviewer profile</h3>
        {hasRun ? null : (
          <p>
            <strong>Not started</strong> · no review run exists yet
          </p>
        )}
        <p className="hint">
          Choose the skills and writing style for{" "}
          {hasRun ? "the next review" : "this review"}. Skills and style come
          from an approved, versioned profile.
        </p>
        <p className="hint">
          Starting a review publishes its findings through the connected GitHub
          App when this change is a pull request.
        </p>
        <label htmlFor="reviewer-profile-mode">
          Profile selection
          <select
            id="reviewer-profile-mode"
            value={mode}
            disabled={disabled}
            onChange={(event) => {
              setMode(event.target.value);
            }}
          >
            <option value="project-default">Use project default</option>
            <option value="specific-profile">Select an approved profile</option>
          </select>
        </label>
        {policy === null ? (
          <p className="hint">Project default has not been loaded.</p>
        ) : policy.defaultReviewerProfile == null ? (
          <p className="hint">
            No default profile is configured. Reviews use the operator&rsquo;s
            existing review protocols.
          </p>
        ) : (
          <p className="hint">
            Project default: {policy.defaultReviewerProfile.protocolId} ·{" "}
            {policy.defaultReviewerProfile.contentDigest}
          </p>
        )}
        {mode !== "specific-profile" ? null : (
          <>
            <label htmlFor="reviewer-profile-id">
              Profile ID
              <input
                id="reviewer-profile-id"
                value={protocolId}
                disabled={disabled}
                onChange={(event) => {
                  setProtocolId(event.target.value);
                }}
                placeholder="team-reviewer"
              />
            </label>
            <label htmlFor="reviewer-profile-digest">
              Approved package digest
              <input
                id="reviewer-profile-digest"
                value={contentDigest}
                disabled={disabled}
                onChange={(event) => {
                  setContentDigest(event.target.value);
                }}
                aria-describedby="reviewer-profile-help"
              />
            </label>
            <p className="hint" id="reviewer-profile-help">
              Use the exact 64-character SHA-256 digest of an approved release.
              Unavailable or unsupported profiles fail without switching to
              another profile.
            </p>
            <button
              className="button secondary"
              type="button"
              disabled={disabled || !valid || policy === null}
              onClick={() => void saveDefault(reference)}
            >
              Use this profile as project default
            </button>
          </>
        )}
        <div className="button-row">
          <button
            className="button primary"
            type="button"
            disabled={
              disabled ||
              !canStart ||
              policy === null ||
              (mode === "specific-profile" && !valid)
            }
            onClick={() =>
              void onStart(mode === "specific-profile" ? reference : undefined)
            }
          >
            {hasRun ? "Start another review" : "Start review"}
          </button>
          <button
            className="button tertiary"
            type="button"
            disabled={disabled}
            onClick={() => void refreshPolicy()}
          >
            Refresh project default
          </button>
          {policy?.defaultReviewerProfile == null ? null : (
            <button
              className="button tertiary"
              type="button"
              disabled={disabled}
              onClick={() => void saveDefault(null)}
            >
              Clear project default
            </button>
          )}
        </div>
        {error === "" ? null : <p role="alert">{error}</p>}
        <p role="status">{message}</p>
      </div>
    </section>
  );
}
